/**
 * The in-product Help models. Mirrors of the API DTOs in
 * ISG.Web.Framework.API/Models/ProB.
 *
 * These arrive as JSON, so member ORDER here is cosmetic - unlike the C# side, where
 * the readers are positional and order is contract. Member NAMES are the contract:
 * they must stay camel-cased matches of the C# property names or the values arrive
 * undefined, silently.
 */

/**
 * A Help node kind - 'def', 'nav' or 'struct'.
 */
export class HelpType {
  code?: string | null;
  parentCode?: string | null;
  name?: string | null;
  sortOrder?: number | null;
  itemCount?: number | null;
}

/**
 * A Help content category.
 *
 * A DIFFERENT axis from HelpType. The type says what kind of node this is in the tree
 * and the CMS needs it to lay the structure out; the category is a content label an
 * author chooses, and the tree does not care about it.
 *
 * The list can legitimately be empty - helpCategoryCode has a foreign key to the
 * table, so while there are no rows NULL is the only permitted value and the picker
 * stays disabled.
 */
export class HelpCategory {
  code?: string | null;
  name?: string | null;
  sortOrder?: number | null;

  /** How many items use the category - shown so a category in use is obvious. */
  itemCount?: number | null;
}

/**
 * One gap in the Help content, as dbo.vw_pb_HelpCoverage defines it.
 *
 * The view owns what counts as a gap. These rows are displayed as given: grouped and
 * relabelled for reading, never filtered or recomputed. The CMS used to derive the
 * same three issues from the tree in TypeScript, which put the definition in two
 * places, and this type exists to end that.
 */
export class HelpCoverageRow {
  /** 'UnreachableItem', 'DuplicateHint' or 'EmptyItem'. */
  issue?: string | null;

  /**
   * The item the gap is about - use this to open it, rather than matching the
   * breadcrumb text.
   *
   * For 'DuplicateHint' it is the lowest id of the colliding group, because the gap is
   * the collision rather than one row.
   */
  helpItemId?: string | null;

  /** What identifies the gap to a person - a breadcrumb, or the contested hint. */
  identifier?: string | null;

  /** The item's label, or how many items claim the hint. */
  detail?: string | null;

  /** How many items the row accounts for. Always 1 except for 'DuplicateHint'. */
  occurrences?: number | null;
}

/**
 * A Help article as a reader may see it.
 *
 * The reader VIEW still carries no authorship columns, so a careless projection
 * cannot leak them. The last-editor NAME is resolved inside the read procedure and
 * appended, which is what lets the article show it without reopening the view.
 */
export class HelpItem {
  id?: string | null;
  parentId?: string | null;
  helpTypeCode?: string | null;
  helpCategoryCode?: string | null;
  label?: string | null;

  /**
   * The article body, as author-supplied HTML.
   *
   * Rendered with [innerHTML] and bypassSecurityTrustHtml, which is needed for the
   * iframe video embeds and is safe ONLY because the API sanitises this on write.
   * See the call site in help-article for the full note.
   */
  displayMarkup?: string | null;

  sortOrder?: number | null;

  /** Shown beside the article heading. */
  lastUpdatedOn?: string | null;

  /**
   * Pre-built trail, e.g. 'User Guide > IT Services > Towers'. Computed server-side
   * from the parent chain - use it as given rather than rebuilding it here, and note
   * it deliberately carries no product-name root crumb.
   */
  breadcrumb?: string | null;

  sectionId?: string | null;
  sectionLabel?: string | null;

  /** 0 section, 1 tab, 2 page, 3 sub-page. */
  depth?: number | null;

  /**
   * Opaque concurrency token to send back when saving this item.
   *
   * An opaque string of digits. Deliberately NOT date-shaped: InterceptorDateHttp
   * replaces any ISO-8601-looking string in any response with a Date, so a
   * date-shaped token was sent back as an ISO string with a Z and every save after
   * the first was refused. Do not parse, display or compare this; use lastUpdatedOn
   * for display.
   */
  concurrencyToken?: string | null;

  /**
   * Display name of whoever last changed the article.
   *
   * Undefined unless the reader endpoint supplies it, so the article shows the
   * date alone rather than breaking when it is absent.
   */
  lastUpdatedByName?: string | null;
}


/**
 * A Help article as an administrator sees it: the reader fields plus the visibility
 * flag, the context hints and authorship.
 *
 * Only ever populated from the admin endpoints. The reader endpoints cannot return
 * these fields - the projections are separate controllers server-side precisely so
 * that cannot be undone by adding a flag.
 */
export class HelpItemAdmin extends HelpItem {

  /** 1 visible to readers, 0 parked by an administrator. */
  isActive?: boolean | null;

  /** The application route this item provides help for. */
  linkUrlPath?: string | null;

  /** The context key this item answers to, e.g. 'tower.workplace'. */
  linkVarName?: string | null;

  lastModifiedBy?: string | null;
  lastModifiedByName?: string | null;
  lastModifiedByEmail?: string | null;

  /**
   * Number of immediate children. Drives the delete confirmation, because a delete
   * cascades over the whole subtree and is permanent.
   */
  childCount?: number | null;
}

/**
 * One node of the Help hierarchy, driving the Help navigation.
 *
 * Has no displayMarkup and must not gain one. The server holds the navigation
 * structure and the article body in the same table row, the navigation loads on every
 * Help open, and carrying article bodies with it is the one performance mistake this
 * design permits. `hasContent` answers "is there a body" without fetching one.
 */
export class HelpTreeNode {
  id?: string | null;
  parentId?: string | null;
  sectionId?: string | null;
  sectionLabel?: string | null;
  helpTypeCode?: string | null;
  helpTypeName?: string | null;
  helpCategoryCode?: string | null;
  label?: string | null;

  /**
   * Depth below the section root. The layout keys off THIS, not off helpTypeCode -
   * the content tree mixes 'nav' and 'struct' at the same depth, so the type code
   * does not identify the level.
   */
  depth?: number | null;

  sortOrder?: number | null;
  isActive?: boolean | null;
  breadcrumb?: string | null;

  /** Whether the node has an article body, without fetching it. */
  hasContent?: boolean | null;

  childCount?: number | null;
  linkUrlPath?: string | null;
  linkVarName?: string | null;
  lastModifiedOn?: string | null;

  /**
   * Opaque concurrency token for saving this node.
   *
   * On the tree as well as on the item because the structure pane saves FROM the
   * tree - dragging a node re-parents it, which is an update and needs a token.
   * Sending none would let a drag overwrite a concurrent edit. Do not parse or
   * display it.
   */
  concurrencyToken?: string | null;
}

/**
 * One step of an item's ancestor trail, root first. Says which branches to expand
 * without a second round trip.
 */
export class HelpAncestor {
  id?: string | null;
  parentId?: string | null;
  label?: string | null;
  depth?: number | null;
  helpTypeCode?: string | null;
}

/**
 * A sibling article with content, offered alongside a resolved help item.
 */
export class HelpRelated {
  id?: string | null;
  label?: string | null;
  sortOrder?: number | null;
}

/**
 * One Help search hit.
 */
export class HelpSearchResult {
  id?: string | null;
  label?: string | null;
  breadcrumb?: string | null;
  sectionId?: string | null;
  sectionLabel?: string | null;
  helpTypeCode?: string | null;
  lastModifiedOn?: string | null;

  /** Relevance. Ranked so a title hit always beats a body hit. */
  rank?: number | null;

  /**
   * Plain-text extract around the first match. Already tag-stripped server-side, so
   * bind it as text - never as HTML.
   */
  snippet?: string | null;
}

/**
 * The article resolved for a screen context, with the two fields that say why.
 */
export class HelpContextItem {
  id?: string | null;
  parentId?: string | null;
  helpTypeCode?: string | null;
  helpCategoryCode?: string | null;
  label?: string | null;
  displayMarkup?: string | null;
  lastUpdatedOn?: string | null;
  breadcrumb?: string | null;
  sectionId?: string | null;
  sectionLabel?: string | null;

  /** Which hint won - useful when help opens the wrong article during authoring. */
  matchedOn?: string | null;

  /** How specific the winning hint was. */
  matchScore?: number | null;

  /** True when nothing matched and generic content was substituted. */
  isFallback?: boolean | null;
}
